import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import BottleMenu from "@/components/BottleMenu";
import { cartTotals, formatBottleNotes } from "@/lib/bottles";

const META = {
  smirnoff: { color: "#FF1B8D", tierKey: "book.tier.standard" },
  ciroc: { color: "#FF6BC6", tierKey: "book.tier.premium" },
  belvedere: { color: "#FFC857", tierKey: "book.tier.exclusive" },
};

export default function Tables() {
  const { t } = useLang();
  const [tables, setTables] = useState([]);
  const [cart, setCart] = useState({});

  useEffect(() => {
    api.get("/tables").then((r) => setTables(r.data)).catch(() => {});
  }, []);

  const inc = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) =>
    setCart((c) => {
      const n = { ...c };
      if ((n[id] || 0) > 1) n[id] -= 1;
      else delete n[id];
      return n;
    });

  const totals = useMemo(() => cartTotals(cart), [cart]);

  const bookHref = useMemo(() => {
    const notes = formatBottleNotes(cart);
    return notes
      ? `/book?type=barorder&notes=${encodeURIComponent(notes)}`
      : "/book?type=barorder";
  }, [cart]);

  return (
    <div data-testid="tables-page" className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-12 pt-12 md:pt-24 pb-36 md:pb-32 overflow-x-hidden">
      <h1 className="sr-only">{t("tables.overline")} — {t("tables.title.1")} {t("tables.title.2")}</h1>
      <img
        src="/banners/pick-your-table.png"
        alt="VIP Bottle Service — Pick Your Table"
        data-testid="tables-hero-banner"
        loading="eager"
        decoding="async"
        className="block w-full max-w-[900px] h-auto -ml-2 md:-ml-4"
      />
      <p className="text-white/65 mt-4 max-w-2xl text-sm md:text-base">{t("tables.sub")}</p>

      <div className="mt-10 md:mt-14 flex flex-col gap-4 md:gap-5">
        {/* SVG chroma-key filter — drops black bg on bottle photos */}
        <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
          <defs>
            <filter id="tables-bottle-chroma" colorInterpolationFilters="sRGB">
              <feColorMatrix
                type="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        1 1 1 0 -0.55"
              />
              <feComponentTransfer>
                <feFuncA type="linear" slope="25" intercept="-8" />
              </feComponentTransfer>
            </filter>
          </defs>
        </svg>

        {tables.map((tt) => {
          const meta = META[tt.id] || META.ciroc;
          const featured = tt.id === "ciroc";
          const bottleSrc =
            tt.id === "smirnoff"
              ? "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/v2xnjob5_2251D45F-704E-4AD3-8985-41807EF1B1F5.png"
              : tt.id === "ciroc"
              ? "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/x7b5d1n0_B58A2135-4280-40BA-A51B-073205FAFD33.png"
              : "https://customer-assets.emergentagent.com/job_soho69-events/artifacts/6m3o2whn_012B99D8-A70B-4BEE-9339-CDF5B9254A7A.png";
          return (
            <div
              key={tt.id}
              data-testid={`table-card-${tt.id}`}
              className="relative rounded-3xl group transition"
              style={{
                background:
                  "linear-gradient(90deg, rgba(255,27,141,0.08) 0%, rgba(0,0,0,0.55) 60%, rgba(255,27,141,0.08) 100%)",
                border: `1px solid ${featured ? "rgba(255,27,141,0.7)" : "rgba(255,27,141,0.3)"}`,
                boxShadow: featured
                  ? "0 0 24px rgba(255,27,141,0.35), inset 0 0 24px rgba(255,27,141,0.12)"
                  : "inset 0 0 18px rgba(255,27,141,0.08)",
              }}
            >
              <div className="flex items-stretch min-h-[150px] md:min-h-[170px]">
                {/* LEFT — tier + name + small price + capacity */}
                <div className="flex-1 p-4 md:p-7 flex flex-col justify-center min-w-0">
                  <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.36em]" style={{ color: meta.color }}>
                    {t(meta.tierKey)}
                  </div>
                  <div className="font-heading text-xl md:text-5xl lg:text-6xl text-white mt-1.5 md:mt-2 leading-[0.95]">
                    {tt.name.toUpperCase()}
                  </div>
                  <div className="mt-2 md:mt-3 flex items-baseline gap-1">
                    <span className="font-display italic text-base md:text-lg" style={{ color: "#FF6BC6" }}>€</span>
                    <span className="font-heading text-xl md:text-2xl" style={{ color: "#FF6BC6" }}>{tt.price_eur}</span>
                  </div>
                  <div className="text-white/70 text-xs md:text-sm mt-1.5">{tt.capacity}</div>
                </div>

                {/* CENTER — big gold elegant price + RESERVE link (desktop) */}
                <div className="hidden md:flex flex-col items-center justify-center px-4 lg:px-8 shrink-0 gap-2">
                  <div
                    className="font-heading leading-none tracking-tight"
                    style={{
                      fontSize: "clamp(2.6rem, 5vw, 4.5rem)",
                      fontWeight: 900,
                      color: "#FF1B8D",
                      textShadow: "0 0 4px rgba(255,27,141,0.25), 0 2px 0 rgba(0,0,0,0.45)",
                    }}
                  >
                    €{tt.price_eur}
                  </div>
                  <Link
                    to="/book?type=barorder"
                    data-testid={`table-book-btn-${tt.id}`}
                    className="inline-flex items-center gap-1.5 text-[11px] md:text-xs font-bold uppercase tracking-[0.36em] text-neon hover:text-white transition"
                  >
                    {t("tables.reserve")} <ArrowUpRight size={14} />
                  </Link>
                </div>

                {/* RIGHT — bottle photo */}
                <div className="flex shrink-0 w-24 md:w-32 lg:w-44 xl:w-52 relative items-center justify-center self-stretch">
                  <div
                    className="absolute inset-y-0 left-0 w-px"
                    style={{ background: "linear-gradient(180deg, transparent 0%, rgba(255,27,141,0.4) 50%, transparent 100%)" }}
                  />
                  <div
                    className="absolute inset-0 m-auto pointer-events-none"
                    style={{
                      width: "120%",
                      height: "180%",
                      background:
                        "radial-gradient(ellipse at center, rgba(255,27,141,0.4) 0%, rgba(255,27,141,0.18) 40%, transparent 70%)",
                      filter: "blur(14px)",
                    }}
                  />
                  <img loading="lazy" decoding="async"                     src={bottleSrc}
                    alt={`${tt.name} bottle`}
                    data-testid={`tables-bottle-${tt.id}`}
                    draggable={false}
                    className="absolute left-1/2 -translate-x-1/2 w-auto select-none pointer-events-none"
                    style={{
                      top: tt.id === "ciroc" ? "32px" : "50%",
                      left: "50%",
                      transform: tt.id === "ciroc" ? "translateX(-50%)" : "translate(-50%, -50%)",
                      height: tt.id === "belvedere" ? "120%" : tt.id === "ciroc" ? "auto" : "108%",
                      maxHeight: tt.id === "belvedere" ? "210px" : tt.id === "ciroc" ? "175px" : "180px",
                      zIndex: 5,
                      filter:
                        "url(#tables-bottle-chroma) drop-shadow(0 0 14px rgba(255,27,141,0.7)) drop-shadow(0 0 32px rgba(255,27,141,0.45))",
                    }}
                  />
                </div>
              </div>

              {/* Mobile-only Reserve Now bar */}
              <Link
                to="/book?type=barorder"
                data-testid={`table-book-mobile-${tt.id}`}
                className="md:hidden flex items-center justify-between gap-3 px-5 py-3.5 border-t border-neon/30 text-neon font-bold uppercase tracking-[0.32em] text-xs"
              >
                {t("tables.reserve")} <ArrowUpRight size={16} />
              </Link>

              {featured && (
                <span
                  className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-[0.28em] text-black px-2.5 py-0.5 rounded-full"
                  style={{ background: "linear-gradient(135deg,#FF1B8D,#FF6BC6)" }}
                >
                  ★ {t("tables.mostWanted")}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-14 md:mt-20 grid md:grid-cols-3 gap-3 md:gap-6 text-sm text-white/75">
        {["1", "2", "3"].map((n) => (
          <div key={n} className="glass rounded-4xl p-5 md:p-6">
            <div className="font-heading text-3xl text-neon">0{n}</div>
            <div className="mt-2">{t(`tables.step.${n}`)}</div>
          </div>
        ))}
      </div>

      <BottleMenu cart={cart} onInc={inc} onDec={dec} />

      {/* Sticky cart bar */}
      {totals.count > 0 && (
        <div
          data-testid="bottle-cart-bar"
          className="fixed left-0 right-0 bottom-0 z-30 px-3 pb-4 pt-3 md:px-12 md:pb-6 md:pt-4"
          style={{
            background:
              "linear-gradient(180deg, rgba(31,15,56,0) 0%, rgba(31,15,56,0.92) 30%, rgba(31,15,56,0.98) 100%)",
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          }}
        >
          <div className="max-w-[900px] mx-auto surface rounded-full px-3 md:px-5 py-2.5 md:py-3 flex items-center gap-3 shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
            <span className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "linear-gradient(135deg,#FF1B8D,#FF6BC6)" }}>
              <ShoppingBag size={18} className="text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.28em] text-neon font-bold">
                {totals.count} {t("bottles.selected")}
              </div>
              <div className="text-white truncate text-sm md:text-base">
                {totals.items.map((x) => `${x.qty}× ${x.name}`).join(" · ")}
              </div>
            </div>
            <div className="text-right hidden sm:block shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-white/50">{t("bottles.from")}</div>
              <div className="font-heading text-2xl text-gold">€{totals.sum}</div>
            </div>
            <Link
              to={bookHref}
              data-testid="bottle-cart-book"
              className="btn-pink !py-3 !px-4 md:!px-6 text-xs md:text-sm shrink-0"
            >
              {t("bottles.book")} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
